import { useRef } from "react";

const GST_RATE = 0.18;
const PER_VFC_BASE = 350;

/**
 * ReceiptModal
 * Props:
 *   receipt   — ServiceCharge record from API (full or per-VFC)
 *   vfcFilter — if set, show receipt only for this single { vfcId, vfcName }
 *   onClose   — close handler
 */
const ReceiptModal = ({ receipt, vfcFilter, onClose }) => {
  const printRef = useRef();

  if (!receipt) return null;

  /* ── Derive receipt data ─────────────────────────────────────────── */
  const isSingleVfc = !!vfcFilter;
  const vfcList     = isSingleVfc
    ? [vfcFilter]
    : (receipt.vfcIds || []).map(v => ({ vfcId: v._id || v, vfcName: v.vfcName || v }));

  const vfcCount    = vfcList.length || 1;
  const totalAmount = isSingleVfc
    ? Math.round(receipt.totalAmount / ((receipt.vfcIds?.length || 1)))
    : (receipt.totalAmount || 0);
  const amountPaid  = Math.round(totalAmount / (1 + GST_RATE));
  const gstAmount   = totalAmount - amountPaid;

  const date = receipt.paidOn
    ? new Date(receipt.paidOn).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

  const dateObj   = receipt.paidOn ? new Date(receipt.paidOn) : new Date();
  const yr        = dateObj.getMonth() >= 3 ? dateObj.getFullYear() : dateObj.getFullYear() - 1;
  const financialYear = receipt.financialYear || `${yr}-${yr + 1}`;

  const paymentId = receipt.razorpayPaymentId || receipt.transactionId || "—";
  const orderId   = receipt.razorpayOrderId   || "—";

  // For per-VFC view, look up the individual invoice number from vfcReceipts
  const perVfcInvoiceNo = isSingleVfc
    ? (receipt.vfcReceipts || []).find(vr => String(vr.vfcId?._id || vr.vfcId) === String(vfcFilter?.vfcId))?.invoiceNo
    : null;
  const invoiceNo = perVfcInvoiceNo || receipt.invoiceNo || "—";
  const rangeName = receipt.rangeId?.rangeName || "—";

  /* ── Print handler ───────────────────────────────────────────────── */
  const handlePrint = () => {
    const content   = printRef.current.innerHTML;
    const printWin  = window.open("", "_blank", "width=800,height=700");
    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt ${invoiceNo}</title>
          <meta charset="utf-8"/>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; font-size: 13px; color: #1A202C; background: #fff; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => { printWin.print(); printWin.close(); }, 300);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(10,15,25,0.7)",
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{
        background: "#fff", borderRadius: 16,
        width: "100%", maxWidth: 680,
        maxHeight: "94vh", display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(0,0,0,0.35)",
        overflow: "hidden",
      }}>

        {/* ── Modal toolbar ── */}
        <div style={{
          padding: "12px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #E2E8F0", background: "#F8FAFC", flexShrink: 0,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#2D3748" }}>
            {isSingleVfc ? `Receipt — ${vfcFilter.vfcName}` : `Receipt — ${invoiceNo}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handlePrint} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "7px 16px",
              background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
              color: "#fff", border: "none", borderRadius: 8,
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 2px 6px rgba(46,125,50,0.3)",
            }}>
              🖨️ Print / Save PDF
            </button>
            <button onClick={onClose} style={{
              width: 32, height: 32, border: "1px solid #E2E8F0",
              borderRadius: 8, background: "#fff", cursor: "pointer",
              fontSize: 18, color: "#718096", display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>✕</button>
          </div>
        </div>

        {/* ── Scrollable receipt body ── */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          <div ref={printRef} style={{ padding: "32px 40px", background: "#fff" }}>

            {/* ── Header ── */}
            <div style={{
              background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
              borderRadius: 12, padding: "20px 28px",
              marginBottom: 20,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 2 }}>
                  Thrayam Solutions Private Limited
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.78)", lineHeight: 1.6 }}>
                  11-1092, Edavattam, Thirunanthikarai<br />
                  Kulasekharam, Kanyakumari, Tamil Nadu — 629161<br />
                  GSTIN: 33AALCT0822B1ZV
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(255,255,255,0.9)", letterSpacing: 0.5, textTransform: "uppercase" }}>
                  Tax Invoice
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
                  GST Receipt
                </div>
              </div>
            </div>

            {/* ── Invoice meta + Bill To ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

              {/* Invoice details */}
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#2E7D32", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
                  Invoice Details
                </div>
                {[
                  { label: "Invoice No",     value: invoiceNo },
                  { label: "Date",           value: date },
                  { label: "Financial Year", value: `FY ${financialYear}` },
                  { label: "SAC Code",       value: "998313" },
                  { label: "Payment Method", value: receipt.paymentMethod || "RAZORPAY" },
                  { label: "Payment ID",     value: paymentId },
                  { label: "Order ID",       value: orderId },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #EDF2F7" }}>
                    <span style={{ fontSize: 11, color: "#718096" }}>{label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#1A202C", maxWidth: 160, textAlign: "right", wordBreak: "break-all" }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Bill To */}
              <div style={{ background: "#F0FFF4", border: "1px solid #C8E6C9", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#2E7D32", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
                  Bill To
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#1A202C", marginBottom: 6 }}>
                  {isSingleVfc ? vfcFilter.vfcName : vfcList.map(v => v.vfcName).join(", ")}
                </div>
                <div style={{ fontSize: 12, color: "#4A5568", lineHeight: 1.7 }}>
                  <div><strong>Range:</strong> {rangeName}</div>
                  <div><strong>VFCs:</strong> {vfcList.length}</div>
                  <div><strong>FY:</strong> {financialYear}</div>
                </div>
              </div>
            </div>

            {/* ── VFC table ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{
                background: "#1B5E20", color: "#fff",
                fontSize: 11, fontWeight: 700, letterSpacing: 1,
                textTransform: "uppercase", padding: "8px 16px",
                borderRadius: "8px 8px 0 0",
              }}>
                Service Details
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#F0FFF4" }}>
                    <th style={{ padding: "9px 12px", textAlign: "left",  color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>#</th>
                    <th style={{ padding: "9px 12px", textAlign: "left",  color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>VFC Name</th>
                    <th style={{ padding: "9px 12px", textAlign: "left",  color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>SAC</th>
                    <th style={{ padding: "9px 12px", textAlign: "left",  color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>Description</th>
                    <th style={{ padding: "9px 12px", textAlign: "right", color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>Base (₹)</th>
                    <th style={{ padding: "9px 12px", textAlign: "right", color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>GST 18% (₹)</th>
                    <th style={{ padding: "9px 12px", textAlign: "right", color: "#2E7D32", fontWeight: 700, border: "1px solid #C8E6C9" }}>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {vfcList.map((vfc, i) => {
                    const base  = Math.round(amountPaid / vfcCount);
                    const gst   = Math.round(gstAmount  / vfcCount);
                    const total = base + gst;
                    return (
                      <tr key={vfc.vfcId || i} style={{ background: i % 2 === 0 ? "#fff" : "#F8FAFC" }}>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", color: "#718096" }}>{i + 1}</td>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", fontWeight: 700, color: "#1A202C" }}>{vfc.vfcName || "—"}</td>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", color: "#4A5568", whiteSpace: "nowrap" }}>998313</td>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", color: "#4A5568" }}>
                          Annual Maintenance Fee — VFC Management SaaS Platform FY {financialYear}
                        </td>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", textAlign: "right", color: "#1A202C" }}>{base.toLocaleString("en-IN")}</td>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", textAlign: "right", color: "#1A202C" }}>{gst.toLocaleString("en-IN")}</td>
                        <td style={{ padding: "8px 12px", border: "1px solid #E2E8F0", textAlign: "right", fontWeight: 700, color: "#1B5E20" }}>{total.toLocaleString("en-IN")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Amount summary (right-aligned like a real invoice) ── */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
              <div style={{ width: 320, border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
                {[
                  { label: `Sub-total (${vfcList.length} VFC × ₹${PER_VFC_BASE})`, value: `₹ ${amountPaid.toLocaleString("en-IN")}` },
                  { label: "CGST @ 9%",   value: `₹ ${Math.round(gstAmount / 2).toLocaleString("en-IN")}` },
                  { label: "SGST @ 9%",   value: `₹ ${Math.round(gstAmount / 2).toLocaleString("en-IN")}` },
                ].map(({ label, value }, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between",
                    padding: "9px 16px", borderBottom: "1px solid #EDF2F7",
                    background: "#F8FAFC",
                  }}>
                    <span style={{ fontSize: 12, color: "#718096" }}>{label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#2D3748" }}>{value}</span>
                  </div>
                ))}
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "13px 16px", background: "#1B5E20",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>TOTAL AMOUNT PAID</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>₹ {totalAmount.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>

            {/* Payment success badge */}
            <div style={{
              background: "#E8F5E9", border: "1px solid #C8E6C9",
              borderRadius: 10, padding: "14px 20px", textAlign: "center",
              marginBottom: 20,
            }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#1B5E20", marginBottom: 2 }}>
                ✅ PAYMENT SUCCESSFUL
              </div>
              <div style={{ fontSize: 12, color: "#388E3C" }}>
                Payment received and verified on {date}
              </div>
            </div>

            {/* ── Footer ── */}
            <div style={{ borderTop: "1px dashed #CBD5E0", paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div style={{ fontSize: 11, color: "#A0AEC0", lineHeight: 1.7 }}>
                <div>This is a computer-generated invoice and does not require a signature.</div>
                <div>For queries: <strong style={{ color: "#718096" }}>support@thrayam.com</strong></div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#A0AEC0" }}>Powered by</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1B5E20" }}>Thrayam Solutions Pvt. Ltd.</div>
                <div style={{ fontSize: 10, color: "#A0AEC0" }}>GSTIN: 33AALCT0822B1ZV</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
